import { useReduxState } from "Shared/redux/useReduxState";
import { InputItem, mapToKenticoKontent } from "./KenticoKontentMapper";
import { mapUserInfo } from "ComponentMappers/UserInfoMapper";
import { PcraLoginInfoModalType } from "Components/pcra-login-info-modal/peru-compra-login-info-modal";
import { deleteLoginInfo } from "Services/common";
import { ROLE_CODES } from "Services/constants";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import { ApiUserInfo } from "Shared/models/ApiUserInfo";
import { LoginInfo } from "Shared/models/LoginInfo";
import { MappedUserInfo } from "Shared/models/MappedUserInfo";
import { commonDebug } from "../../bootstrap";
import { useLogger } from "Services/logging/logger/useLogger";
import { UserLoginRegisterEventData } from "Services/logging/logger/types-and-interfaces/UserLoginEvent";

export interface Props {
	closeMobileMenu: () => void;
}

export type CaptchaStatus = "loading" | "loaded" | "error";

export const useAppInitializer = (props: Props) => {
	const { logEvent } = useLogger();

	const [_userInfo, setUserInfo] = useReduxState("userInfo");
	const [_kontent, setKontent] = useReduxState("kontent");
	const [_captchaStatus, setCaptchaStatus] = useReduxState("captchaStatus");

	const getUserTypeForLoginInfoModal = (userRoleCode: string): PcraLoginInfoModalType =>
		userRoleCode === "PECG" ? "gestor" : userRoleCode === "PECD" ? "delegado" : "none";

	const getUserInfo = async () => {
		const baseUrl = "/api/UserData/UserInfo";
		const fetchOptions: RequestInit = { method: "GET", credentials: "include", mode: "cors" };
		const fetchParameters = { options: fetchOptions, url: baseUrl };

		try {
			const result = await fetch(fetchParameters.url, fetchParameters.options);

			if (result.ok && result.status === 200) {
				const data = (await result.json()) as ApiUserInfo;
				return mapUserInfo(data);
			}

			return { RoleCode: ROLE_CODES.WEB_ANONYMOUS };
		} catch (e) {
			commonDebug.error(e);
			return { RoleCode: ROLE_CODES.WEB_ANONYMOUS };
		}
	};

	const getLoginInfo = async () => {
		const baseUrl = "/api/UserData/LoginInfo";
		const fetchOptions: RequestInit = { method: "GET", credentials: "include", mode: "cors" };
		const fetchParameters = { options: fetchOptions, url: baseUrl };

		try {
			const result = await fetch(fetchParameters.url, fetchParameters.options);

			if (result.ok) {
				const data = (await result.json()) as LoginInfo;
				return data;
			}

			return undefined;
		} catch (e) {
			commonDebug.error(e);
			return undefined;
		}
	};

	const getDeviceId = async (): Promise<string> => {
		const baseUrl = "/api/UserData/DeviceId";
		const fetchOptions: RequestInit = { method: "GET", credentials: "include", mode: "cors" };
		const fetchParameters = { options: fetchOptions, url: baseUrl };

		try {
			const result = await fetch(fetchParameters.url, fetchParameters.options);

			if (result.ok) {
				const data = (await result.json()) as { Id: string };
				return data.Id;
			}

			return undefined;
		} catch (e) {
			commonDebug.error(e);
			return undefined;
		}
	};

	const handleLoginInfoResult = async (loginInfo: LoginInfo) => {
		if (loginInfo?.tealium) {
			logEvent<UserLoginRegisterEventData>(
				loginInfo.tealium.action.toLowerCase() === "login" ? "user_login" : "user_register",
				{ loginInfo }
			);
		}

		if (loginInfo?.chileCompra) {
			props.closeMobileMenu();
			PUB_SUBS.ChileCompraLoginInfoModalOpened.publish({});
			return;
		}

		if (loginInfo?.peruCompra) {
			props.closeMobileMenu();
			PUB_SUBS.PcraLoginInfoModalOpened.publish({
				modalType: getUserTypeForLoginInfoModal(loginInfo?.tealium?.userRole),
			});
			return;
		}

		try {
			await deleteLoginInfo();
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error("Could not delete login info", e);
		}
	};

	const handleUserInfoResult = (userInfoResult: MappedUserInfo) => {
		if (userInfoResult?.BancoEstado?.Category > 0) validateRestrictedUrl();

		setUserInfo(userInfoResult);

		window.UserInfo = userInfoResult;
	};

	const setAmplitudeDeviceId = async () => {
		try {
			const deviceId = await getDeviceId();

			if (deviceId) {
				if (window.amplitude && typeof window.amplitude.setDeviceId === "function") {
					window.amplitude.setDeviceId(deviceId);
				} else {
					const amplitudeInterval = window.setInterval(() => {
						if (
							window.amplitude?.getInstance()?._isInitialized &&
							typeof window.amplitude.setDeviceId === "function"
						) {
							window.clearInterval(amplitudeInterval);
							window.clearTimeout(amplitudeTimeLimit);
							window.amplitude.setDeviceId(deviceId);
						}
					}, 100);

					const amplitudeTimeLimit = window.setTimeout(() => {
						window.clearInterval(amplitudeInterval);
						// eslint-disable-next-line no-console
						console.error("Could not access Amplitude.");
					}, 10000);
				}
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error("Unknown Amplitude/Tealium cookie problem.");
		}
	};

	const validateRestrictedUrl = () => {
		const pathElements = window.location.href.split("/");

		if (pathElements.length < 5) {
			window.location.href = "/cl/es";
			return;
		}

		const countryKey = 3;
		const languageKey = 4;

		if (pathElements[countryKey].toLowerCase() !== "cl" || pathElements[languageKey].toLowerCase() !== "es") {
			pathElements[countryKey] = "cl";
			pathElements[languageKey] = "es";

			const newUrl = pathElements.join("/");

			window.location.href = newUrl;
		}
	};

	const getKontent = async () => {
		const baseUrl = "/GetKenticoNewHome";
		const fetchOptions: RequestInit = { method: "GET", credentials: "include", mode: "cors" };
		const fetchParameters = { options: fetchOptions, url: baseUrl };

		try {
			const result = await fetch(fetchParameters.url, fetchParameters.options);

			if (result.ok) {
				const data = (await result.json()) as InputItem[];
				setKontent(mapToKenticoKontent(data));
				// eslint-disable-next-line no-console
				console.log("KONTENT raw", data);
				// eslint-disable-next-line no-console
				console.log("KONTENT digested", mapToKenticoKontent(data));
			}
		} catch (e) {
			commonDebug.error(e);
		}
	};

	const initCaptcha = () => {
		const grecaptchaTimeLimit = window.setTimeout(() => {
			window.clearInterval(grecaptchaInterval);
			setCaptchaStatus("error");
		}, 3000);

		const grecaptchaInterval = window.setInterval(() => {
			if (grecaptcha?.render) {
				setCaptchaStatus("loaded");
				window.clearInterval(grecaptchaInterval);
				window.clearTimeout(grecaptchaTimeLimit);
			}
		}, 100);
	};

	const init = async () => {
		initCaptcha();
		await getKontent();

		const userInfoResult = await getUserInfo();
		handleUserInfoResult(userInfoResult);

		const loginInfoResult = await getLoginInfo();
		await handleLoginInfoResult(loginInfoResult);

		await setAmplitudeDeviceId();
	};

	return { init };
};
